import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Settings, Save, Loader2, Eye, EyeOff } from "lucide-react";

import { getSettings, updateSettings } from "../../services/firestoreService";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";

// ---------------------------------------------------------------------------
// Section definitions
// ---------------------------------------------------------------------------
const SECTION_DEFS = [
  {
    key: "aboutVisible",
    label: "Tentang",
    description: "Tampilkan bagian Tentang di halaman utama",
  },
  {
    key: "servicesVisible",
    label: "Layanan",
    description: "Tampilkan bagian Layanan di halaman utama",
  },
  {
    key: "portfolioVisible",
    label: "Portfolio",
    description: "Tampilkan bagian Portfolio di halaman utama",
  },
  {
    key: "packagesVisible",
    label: "Paket",
    description: "Tampilkan bagian Paket di halaman utama",
  },
  {
    key: "contactVisible",
    label: "Kontak",
    description: "Tampilkan bagian Kontak di halaman utama",
  },
  {
    key: "testimonialsVisible",
    label: "Testimoni",
    description: "Tampilkan bagian Testimoni di halaman utama",
  },
  {
    key: "pressStripVisible",
    label: "Press Strip",
    description: "Logo media yang tampil berjalan (marquee) di halaman utama",
  },
  {
    key: "certificatesVisible",
    label: "Sertifikat",
    description: "Tampilkan bagian Sertifikat di halaman utama",
  },
];

// ---------------------------------------------------------------------------
// Default settings (all visible)
// ---------------------------------------------------------------------------
const DEFAULT_SETTINGS = {
  aboutVisible: true,
  servicesVisible: true,
  portfolioVisible: true,
  packagesVisible: true,
  contactVisible: true,
  testimonialsVisible: true,
  pressStripVisible: true,
  certificatesVisible: true,
};

// ---------------------------------------------------------------------------
// Settings page
// ---------------------------------------------------------------------------
export default function SettingsPage() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSettings();
      if (data) {
        // Merge only known keys from DEFAULT_SETTINGS
        const merged = { ...DEFAULT_SETTINGS };
        for (const key of Object.keys(DEFAULT_SETTINGS)) {
          if (typeof data[key] === "boolean") {
            merged[key] = data[key];
          }
        }
        setSettings(merged);
      }
    } catch {
      toast.error("Gagal memuat pengaturan");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Handle toggle
  const handleToggle = (key) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return next;
    });
    setDirty(true);
  };

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success("Pengaturan berhasil disimpan");
      setDirty(false);
    } catch {
      toast.error("Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  // Count visible sections
  const visibleCount = Object.values(settings).filter(Boolean).length;
  const totalCount = Object.keys(DEFAULT_SETTINGS).length;

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
          <h1 className="text-2xl font-display text-[#fdfbf7]">Settings</h1>
        </div>
        <p className="text-sm text-[#a6a196] ml-4">
          Kontrol bagian mana yang tampil di halaman utama website.
        </p>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-16 bg-[#15120f] rounded-lg border border-[#d4af37]/10 animate-pulse"
            />
          ))}
        </div>
      ) : (
        <>
          {/* Summary card */}
          <Card className="bg-[#0a0908] border-[#d4af37]/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center">
                  {visibleCount === totalCount ? (
                    <Eye className="w-5 h-5 text-[#d4af37]" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-[#a6a196]" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-[#fdfbf7] font-medium">
                    Visibilitas Bagian
                  </p>
                  <p className="text-xs text-[#a6a196]">
                    {visibleCount} dari {totalCount} bagian terlihat
                  </p>
                </div>
                {/* Progress bar */}
                <div className="flex-1 max-w-[200px] ml-auto hidden sm:block">
                  <div className="h-1.5 bg-[#15120f] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#d4af37] rounded-full transition-all duration-500"
                      style={{
                        width: `${(visibleCount / totalCount) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section toggles */}
          <div className="space-y-3">
            {SECTION_DEFS.map((section) => {
              const isVisible = settings[section.key];
              return (
                <Card
                  key={section.key}
                  className={`bg-[#0a0908] border transition-all duration-200 ${
                    isVisible
                      ? "border-[#d4af37]/20"
                      : "border-[#d4af37]/5 opacity-60"
                  }`}
                >
                  <CardContent className="flex items-center justify-between py-4 px-5">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={section.key}
                          className="text-sm font-medium text-[#fdfbf7] cursor-pointer"
                        >
                          {section.label}
                        </Label>
                        {isVisible ? (
                          <span className="text-[10px] uppercase tracking-wider text-green-400/80 bg-green-400/5 px-1.5 py-0.5 rounded">
                            Terlihat
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider text-[#a6a196]/60 bg-[#a6a196]/5 px-1.5 py-0.5 rounded">
                            Tersembunyi
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#a6a196] mt-0.5">
                        {section.description}
                      </p>
                    </div>
                    <Switch
                      id={section.key}
                      checked={isVisible}
                      onCheckedChange={() => handleToggle(section.key)}
                      className="shrink-0 data-[state=checked]:bg-[#d4af37] data-[state=unchecked]:bg-[#a6a196]/20"
                    />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Save bar */}
          <div className="sticky bottom-0 bg-[#0a0908]/95 backdrop-blur-sm border border-[#d4af37]/10 rounded-lg p-4 flex items-center justify-between">
            <p className="text-xs text-[#a6a196]">
              {dirty
                ? "Ada perubahan yang belum disimpan"
                : "Semua perubahan tersimpan"}
            </p>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="bg-[#d4af37] hover:bg-[#c49f2e] text-[#0a0908] font-medium text-sm disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Simpan Perubahan
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
